import hljs from "highlight.js";
import "katex/dist/katex.css";
import { Marked } from "marked";
import { markedHighlight } from "marked-highlight";
import { createRoot, Root } from "react-dom/client";
import { renderToString } from "react-dom/server";
import * as XLSX from "xlsx";
import { Mermaid } from "../mermaid";
import { downloadImage } from "./action";
import markedKatex from "./marked-katex-extension";
import "./notion-light-enhanced.css";
import { juchatsThinking } from "./plugin/thinking";
import { svgText } from "./svgText";
const IMAGE_SPLIT = "--IMAGES-SPLIT--";

const appInstance = new Map<
  Element,
  {
    app: Root;
    renderContainer: HTMLDivElement;
  }
>();

if (typeof window !== "undefined") {
  // @ts-expect-error 忽略 window.renderMermaid 的类型错误
  window.renderMermaid = function () {
    const renderButton = this as unknown as HTMLDivElement;
    const rendered = renderButton.classList.toggle("rendered");
    const code = `${decodeURIComponent(renderButton.dataset.code!)}`;

    if (renderButton) {
      const codeContainer = renderButton.closest(".language-mermaid");
      if (codeContainer) {
        let renderContainer: HTMLDivElement | undefined;
        if (rendered) {
          codeContainer.childNodes.forEach((child) => {
            if (child.nodeType === 3) {
              child.remove();
            }
          });
          renderContainer = document.createElement("div");
          codeContainer.appendChild(renderContainer);
          const app = createRoot(renderContainer);
          app.render(<Mermaid code={code} />);
          appInstance.set(codeContainer, { app, renderContainer });
        } else {
          const app = appInstance.get(codeContainer);
          app && app.app.unmount();
          app && app.renderContainer.remove();
          const textNode = document.createTextNode(code);
          codeContainer.appendChild(textNode);
        }
      } else {
        console.error(`codeContainer not found`);
      }
    } else {
      console.error(`renderButton not found`);
    }
  };
}

export function generateCodeTitleMarkup(lang: string, code: string) {
  const isMermaid = lang === "mermaid";

  return `<div class="code-title flex items-center">
  <div class="grow flex items-center lang-text animationFadeInFast cursor-pointer" onclick="this.closest('.hljs').classList.toggle('code-block-collapsed');">
    <span class='animationFadeInFast leading-[16px] font-inter'>${lang || "plaintext"}</span>
    <button
      class="expand-and-collapse"
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 9 6" class="w-[8px] h-[8px]"><path d="M4.243 5.657L0 1.414 1.414 0l2.829 2.828L7.07 0l1.414 1.414z" fill="#CDCDCD"  /></svg>
    </button>
  </div>
  <div class="hljs__action">
    ${
      isMermaid
        ? `<div
      class="flex items-center cursor-pointer render-mermaid right-[30px] loading"
      data-code="${encodeURIComponent(code)}"
      onclick="renderMermaid.call(this)"
    >
      <div class="render-mermaid-icon flex-c">
        ${svgText.renderMermaid}
        ${svgText.reverseMeraid}
      </div>
    </div>`
        : ""
    }
    <div
      class="flex items-center hljs__copy__btn  cursor-pointer copy-btn"
      value="${encodeURIComponent(code)}"
    >
      <div class="copy-icon flex-c">
        ${svgText.copy}
        <svg class="copied-icon-svg" width="11" height="8" stroke="#00AB3C" viewBox="0 0 11 8" xmlns="http://www.w3.org/2000/svg"><path d="M10 1L3.75 7.25 1 4.5" stroke="#00AB3C" fill="none" fill-rule="evenodd" stroke-linecap="round" stroke-linejoin="round"></path></svg>
      </div>
    </div>
  </div>
</div>`;
}

const marked = new Marked(
  markedHighlight({
    highlight(code, lang: any) {
      return `${generateCodeTitleMarkup(lang, code)}${hljs.highlightAuto(code, [lang]).value}`;
    },
  })
);

if (typeof window !== "undefined") {
  // @ts-ignore
  window.downloadImage = async function (encodedUrl: string) {
    document.body.classList.add("download-image");
    try {
      const url = decodeURIComponent(encodedUrl);
      const result = await downloadImage(url);

      if (!result.success) {
        throw new Error(result.error);
      }

      // 创建 Blob
      const byteCharacters = atob(result.data!);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: result.contentType });

      // 获取文件扩展名并确定正确的MIME类型
      const ext = url.split(".").pop()?.split("?")[0]?.toLowerCase() || "png";
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `image.${ext}`;
      document.body.appendChild(link); // 某些浏览器需要将链接添加到DOM中
      link.click();
      document.body.removeChild(link); // 清理DOM
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error("下载图片失败:", error);
    }
    document.body.classList.remove("download-image");
  };

  // 添加下载表格的全局函数
  // @ts-ignore
  window.downloadTable = function (button: HTMLElement) {
    const tableContainer = button.closest(".ju-markdown-media-wrapper");
    const table = tableContainer?.querySelector("table");
    if (!table) {
      console.error("找不到表格元素");
      return;
    }

    // 获取表格数据
    const data: string[][] = [];
    const rows = table.querySelectorAll("tr");

    rows.forEach((row) => {
      const rowData: string[] = [];
      const cells = row.querySelectorAll("th, td");
      cells.forEach((cell) => {
        rowData.push(cell.textContent?.trim() || "");
      });
      data.push(rowData);
    });

    try {
      // 创建工作簿
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(data);

      // 设置列宽
      const colWidths = data[0].map((cell) => ({
        wch: Math.min(50, Math.max(10, cell.length * 2)),
      }));
      ws["!cols"] = colWidths;

      // 添加工作表到工作簿
      XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

      // 生成并下载文件
      XLSX.writeFile(wb, "table.xlsx");
    } catch (error) {
      console.error("导出表格失败:", error);
    }
  };
}

marked.use(
  markedKatex({
    throwOnError: false,
    strict: false,
  }),
  {
    extensions: [juchatsThinking],
    renderer: {
      image(href: string, title: string | null, text: string) {
        try {
          const url = new URL(href);
          if (url.origin === "https://api.mapbox.com") {
            const locationURL = new URL(window.location.href);
            if (locationURL.searchParams.get("lang") === "zhCn") {
              url.searchParams.set("language", "zh");
              url.pathname = url.pathname.replace("outdoors-v12", "streets-zh-v1");
            } else {
              url.pathname = url.pathname.replace("outdoors-v12", "streets-v12");
            }
            href = url.toString();
          }
        } catch {}

        return `
          <div class="ju-markdown-image-wrapper">
            <div class="ju-markdown-toolbar">
              <div class="ju-markdown-download-btn" onclick="downloadImage('${encodeURIComponent(href)}')">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M13 13V18.585L14.8284 16.7574L16.2426 18.1716L12 22.4142L7.75736 18.1716L9.17157 16.7574L11 18.585V13H13ZM12 2C15.5934 2 18.5544 4.70761 18.9541 8.19395C21.2858 8.83154 23 10.9656 23 13.5C23 16.3688 20.8036 18.7246 18.0006 18.9776L18.0009 16.9644C19.6966 16.7214 21 15.2629 21 13.5C21 11.567 19.433 10 17.5 10C17.2912 10 17.0867 10.0183 16.8887 10.054C16.9616 9.7142 17 9.36158 17 9C17 6.23858 14.7614 4 12 4C9.23858 4 7 6.23858 7 9C7 9.36158 7.03838 9.7142 7.11205 10.0533C6.91331 10.0183 6.70879 10 6.5 10C4.567 10 3 11.567 3 13.5C3 15.2003 4.21241 16.6174 5.81986 16.934L6.00005 16.9646L6.00039 18.9776C3.19696 18.7252 1 16.3692 1 13.5C1 10.9656 2.71424 8.83154 5.04648 8.19411C5.44561 4.70761 8.40661 2 12 2Z"></path></svg>
              </div>
            </div>
            <img src="${href}" alt="${text}" title="${title}" />
          </div>
        `;
      },
      table(header: string, body: string) {
        return `
          <div class="ju-markdown-media-wrapper">
            <div class="ju-markdown-toolbar">
              <div class="ju-markdown-download-btn " onclick="window.downloadTable(this)">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M13 13V18.585L14.8284 16.7574L16.2426 18.1716L12 22.4142L7.75736 18.1716L9.17157 16.7574L11 18.585V13H13ZM12 2C15.5934 2 18.5544 4.70761 18.9541 8.19395C21.2858 8.83154 23 10.9656 23 13.5C23 16.3688 20.8036 18.7246 18.0006 18.9776L18.0009 16.9644C19.6966 16.7214 21 15.2629 21 13.5C21 11.567 19.433 10 17.5 10C17.2912 10 17.0867 10.0183 16.8887 10.054C16.9616 9.7142 17 9.36158 17 9C17 6.23858 14.7614 4 12 4C9.23858 4 7 6.23858 7 9C7 9.36158 7.03838 9.7142 7.11205 10.0533C6.91331 10.0183 6.70879 10 6.5 10C4.567 10 3 11.567 3 13.5C3 15.2003 4.21241 16.6174 5.81986 16.934L6.00005 16.9646L6.00039 18.9776C3.19696 18.7252 1 16.3692 1 13.5C1 10.9656 2.71424 8.83154 5.04648 8.19411C5.44561 4.70761 8.40661 2 12 2Z"></path></svg>
              </div>
            </div>
            <div class="ju-markdown-table-container">
              <table>
                <thead>${header}</thead>
                <tbody>${body}</tbody>
              </table>
            </div>
          </div>
        `;
      },
    },
  }
);

function replaceCode(originalCodeArr: RegExpMatchArray, newCodeArr: RegExpMatchArray, sourceText: string) {
  for (let i = 0; i < newCodeArr.length; i++) {
    if (originalCodeArr[i].includes("$")) {
      originalCodeArr[i] = originalCodeArr[i].replaceAll("$", "$$$");
    }
    sourceText = sourceText.replaceAll(newCodeArr[i], originalCodeArr[i]);
  }
  return sourceText;
}

function escapeKatex(text: string) {
  const regDouble = /\$\$[^\n`](.+?)\$\$/g;
  const regSingle = /\$[^$\n\d`]([^$]+)\$/g;
  const codeBlock = /```[\s\S]*?```/g;
  const clodeInline = /`.*?`/g;
  const strong = /\*\*(.*?)\*\*/g;

  const originalCodeBlock = text.match(codeBlock);
  const originalCodeInline = text.match(clodeInline);
  const originalStrong = text.match(strong);

  // 把包含公式的 * *  去掉加粗，因为无效
  if (originalStrong) {
    originalStrong.forEach((item) => {
      if (item.includes("(") || item.includes("[") || item.includes("&")) {
        text = text.replaceAll(item, item.replaceAll("**", ""));
      }
    });
  }

  text = text
    .replaceAll(regDouble, ` \$ $1 \$`)
    .replaceAll(regSingle, ` \$ $1 \$ `)
    .replaceAll("\\[", "\n$$$\n")
    .replaceAll("\\]", "\n$$$\n")
    .replaceAll("\\(", " $$")
    .replaceAll("\\)", "$$ ");

  const newCodeBlock = text.match(codeBlock);
  const newCodeInline = text.match(clodeInline);

  if (originalCodeBlock && newCodeBlock) {
    text = replaceCode(originalCodeBlock, newCodeBlock, text);
  }

  if (originalCodeInline && newCodeInline) {
    text = replaceCode(originalCodeInline, newCodeInline, text);
  }
  return text;
}

const renderHtmlByMarked = (str: string) => {
  return (marked.parse(escapeKatex(str)) as string)
    .replace(/<pre>/g, "<pre class='hljs'>")
    .replace(/<a[^>]*>.*?<\/a>/g, (match: any) => {
      if (!match.includes("target=")) {
        return match.replace(">", ' target="_new">');
      } else {
        return match;
      }
    });
};

export const formatCode = (code: string) => {
  return hljs.highlightAuto(code).value;
};

export function renderMarkdown(
  markdown: string,
  modelId: number,
  originalUrls: string[],
  artifacts: null | { id: number; artifacts: string }[]
) {

  // Gemini Image
  try {
    const json = JSON.parse(markdown) as any[];
    const sorted = json.sort((a, b) => {
      if (a.text) {
        return -1;
      }
      return 1;
    });
    markdown = sorted.reduce((result, item) => {
      if (item.text) {
        result += item.text;
      }
      if (item.inlineData) {
        result += `![alt text](${item.inlineData.data})`;
      }
      return result;
    }, "");
  } catch {}

  markdown = markdown.replace(/<juchats-artifacts>(.*?)<\/juchats-artifacts>/g, (match, p1) => {
    try {
      if (!artifacts) return "";
      const obj = JSON.parse(p1);
      const target = artifacts.find((item) => JSON.parse(item.artifacts).title === obj.title);

      return renderToString(
        <div
          data-artifact-id={target?.id || ""}
          className="w-[280px] mt-[-6px] flex cursor-pointer rounded-[10px] bg-[var(--artifacts-card-bg)] p-[10px] text-[var(--artifacts-card-text)] transition-all shadow-[0_0_40px_0_#0000000f] dark:hover:shadow-[0_0_10px_0_#0000001a] hover:shadow-[0_0_40px_0_#0000001a]"
        >
          <div className="h-[40px] w-[40px] flex items-center justify-center rounded-[5px] bg-[#000000] shadow-[0_5px_5px_0_#36445738] dark:shadow-none">
            <img
              src="/herm-chat/artifacts-code.png"
              className="w-[16px] rounded-none"
              alt=""
              style={{ cursor: "pointer" }}
            />
          </div>
          <div className="pl-[10px]">
            <div className="text-[13px] font-[700] font-roboto">{obj.title}</div>
            <div className="text-[12px] text-[var(--artifacts-card-desc)] font-400 font-roboto">
              Click to see fragment
            </div>
          </div>
        </div>
      );
    } catch {
      return "";
    }
  });

  let [str, res] = markdown.split("HERMSTDUIO");

  let relationResource: {
    searchResult: { title: string; link: string; icon: string; position: number }[];
    answer: string;
  } = {
    searchResult: [],
    answer: "",
  };

  try {
    let obj = JSON.parse(res);
    obj.searchResult = JSON.parse(obj.searchResult)
      .filter((item: any) => Boolean(item.position))
      .map((item: any) => ({
        ...item,
        icon: `//s2.googleusercontent.com/s2/favicons?domain=${item.link}`,
      }));
    relationResource = obj;
  } catch {}

  if (str.includes(IMAGE_SPLIT)) {
    const [id, imgText] = markdown.split(IMAGE_SPLIT);
    const origin = originalUrls.find((item) => item.includes(id));
    const url = origin || `${process.env.OSS_URL}/r2tb/${id}.jpg`;
    const imgMarkdown = `![image](${url})`;
    str = imgMarkdown + "\n\n" + imgText;
  }

  let html = "";
  let title = "";
  let filePath = "";
  let code = "";

  if (modelId === 30) {
    let json: any = {};

    try {
      json = JSON.parse(str);
    } catch (error) {
      console.log(error);
      json = {};
    }
    title = json.title;
    filePath = json.file_path;
    code = json.code;
    html = renderHtmlByMarked(json.commentary);
  } else {
    html = renderHtmlByMarked(str);
  }

  return { html, title, relationResource, filePath, code };
}

export const renderQuestion = (question: string, originalUrls: string[]) => {
  try {
    const { text } = JSON.parse(question)[0];
    return text;
  } catch {
    if (question && question.includes(IMAGE_SPLIT)) {
      const [id, imgText] = question.split(IMAGE_SPLIT);
      const imgIDs = id.split(",");

      const imgURLs = imgIDs.map((item) => {
        const origin = originalUrls.find((item) => item.includes(item));
        return origin || `${process.env.OSS_URL}/r2tb/${item}`;
      });

      return (
        <>
          {imgText}
          <div className="ju-markdown-image-wrapper">
            <div className="ju-markdown-toolbar">
              <div
                className="ju-markdown-download-btn"
                onClick={() => {
                  imgURLs.forEach((item) => {
                    // @ts-ignore
                    window.downloadImage(item);
                  });
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M13 13V18.585L14.8284 16.7574L16.2426 18.1716L12 22.4142L7.75736 18.1716L9.17157 16.7574L11 18.585V13H13ZM12 2C15.5934 2 18.5544 4.70761 18.9541 8.19395C21.2858 8.83154 23 10.9656 23 13.5C23 16.3688 20.8036 18.7246 18.0006 18.9776L18.0009 16.9644C19.6966 16.7214 21 15.2629 21 13.5C21 11.567 19.433 10 17.5 10C17.2912 10 17.0867 10.0183 16.8887 10.054C16.9616 9.7142 17 9.36158 17 9C17 6.23858 14.7614 4 12 4C9.23858 4 7 6.23858 7 9C7 9.36158 7.03838 9.7142 7.11205 10.0533C6.91331 10.0183 6.70879 10 6.5 10C4.567 10 3 11.567 3 13.5C3 15.2003 4.21241 16.6174 5.81986 16.934L6.00005 16.9646L6.00039 18.9776C3.19696 18.7252 1 16.3692 1 13.5C1 10.9656 2.71424 8.83154 5.04648 8.19411C5.44561 4.70761 8.40661 2 12 2Z"></path>
                </svg>
              </div>
            </div>
            <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
              {imgURLs.map((item, i) => (
                <img src={item} key={i} alt="" />
              ))}
            </div>
          </div>
        </>
      );
    }

    return question;
  }
};
